import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { T, sans, card, linkBtn } from './VendorDetails';

interface HistoryScan {
  id: string;
  ref?: string | null;
  date: string;
  scan_type: 'fast' | 'deep' | string;
  score: number | null;
  risk_level: string | null;
  change: number | null;
  findings: { critical: number; high: number; medium: number; low: number };
  indicator_count: number;
  trigger: { source: string; by: string | null } | null;
}

// A scan that was started but never finished: listed so failures are visible (no score, nothing to compare).
interface FailedScan { id: string; ref: string | null; date: string; error: string | null; trigger: HistoryScan['trigger'] }

interface Comparison {
  has_comparison: boolean;
  message?: string;
  base_scan?: { id: string; date: string; score: number };
  head_scan?: { id: string; date: string; score: number };
  score_change?: number;
  severity?: Record<'critical' | 'high' | 'medium' | 'low', { from: number; to: number }>;
  new_findings?: { indicator_name: string; category: string; severity: string }[];
  resolved?: { indicator_name: string; category: string; severity: string }[];
  declined?: { indicator_name: string; category: string; severity: string; change: number }[];
  improved?: { indicator_name: string; category: string; severity: string; change: number }[];
}

interface Props {
  vendorId: string;
  vendorName?: string;
  isDark: boolean;
}

type Filter = 'all' | 'fast' | 'deep' | 'dropped';
type Range = '3m' | '6m' | 'all';

const TRIGGER_LABEL: Record<string, string> = {
  manual: 'Manual', scheduled: 'Scheduled', onboarding: 'Onboarding', screening: 'Pre-onboarding screening',
  bulk: 'Bulk upload', fourth_party: 'Fourth-party check',
};
const triggerText = (t: HistoryScan['trigger']) => (t ? `${TRIGGER_LABEL[t.source] || t.source}${t.by ? ` by ${t.by}` : ''}` : null);

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const relative = (iso: string) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  const days = Math.round(mins / 1440);
  return days < 45 ? `${days}d ago` : fmtDate(iso);
};
const apiError = (e: any, fallback: string) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};
// CSV cells starting with = + - @ would run as formulas in a spreadsheet.
const csvCell = (v: unknown) => {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const Icon = ({ d, size = 14, style }: { d: string; size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }}><path d={d} /></svg>
);
const I = {
  diff: 'M6 3v12M18 9v12M6 15a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM18 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM6 6h6a3 3 0 0 1 3 3v0M18 18h-6a3 3 0 0 1-3-3v0',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14',
  close: 'M6 6l12 12M18 6 6 18',
  arrow: 'M9 6l6 6-6 6',
  scan: 'M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 12h10',
};

export default function VendorHistory({ vendorId, vendorName, isDark }: Props) {
  const navigate = useNavigate();
  const historyQuery = useQuery({
    queryKey: ['vendors', 'detail', vendorId, 'scan-history'],
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/scan-history`)).data as { scans: HistoryScan[]; failed?: FailedScan[] },
  });
  const scans: HistoryScan[] = useMemo(() => historyQuery.data?.scans ?? [], [historyQuery.data]);

  const [filter, setFilter] = useState<Filter>('all');
  const [range, setRange] = useState<Range>('all');
  const [selected, setSelected] = useState<string[]>([]);
  const [compareIds, setCompareIds] = useState<[string, string] | null>(null);
  const [shown, setShown] = useState(15);

  const compareQuery = useQuery({
    queryKey: ['vendors', 'detail', vendorId, 'scan-comparison', compareIds?.[0], compareIds?.[1]],
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/scan-comparison`, { params: { base: compareIds![0], head: compareIds![1] } })).data as Comparison,
    enabled: !!compareIds,
  });

  const c = {
    ink: isDark ? '#e2e8f0' : T.ink,
    muted: isDark ? '#94a3b8' : T.slate,
    faint: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr,
    soft: isDark ? '#141414' : T.surface2,
    surface: isDark ? '#0d0d0d' : T.surface,
    accent: isDark ? '#60A5FA' : T.blue,
    green: isDark ? '#4ADE80' : '#15803D',
    amber: isDark ? '#FBBF24' : '#B45309',
    red: isDark ? '#F87171' : '#DC2626',
  };
  const scoreColor = (s: number | null) => (s == null ? c.faint : s >= 80 ? c.green : s >= 60 ? c.amber : c.red);
  const btn = (active = false): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
    border: `1px solid ${active ? (isDark ? '#3B82F6' : T.ink) : c.line}`, background: active ? (isDark ? '#3B82F6' : T.ink) : c.surface, color: active ? '#fff' : c.ink,
  });
  const pill = (fg: string, bg: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', padding: '1px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, color: fg, background: bg, whiteSpace: 'nowrap',
  });
  const redPill = pill(c.red, isDark ? 'rgba(248,113,113,.12)' : '#FEF2F2');
  const amberPill = pill(c.amber, isDark ? 'rgba(251,191,36,.12)' : '#FFFBEB');

  /* ── stats ── */
  const scored = scans.filter(s => s.score != null);
  const latest = scans[0] || null;
  const best = scored.reduce<HistoryScan | null>((b, s) => (!b || s.score! > b.score! ? s : b), null);
  const worst = scored.reduce<HistoryScan | null>((w, s) => (!w || s.score! < w.score! ? s : w), null);
  const average = scored.length ? Math.round(scored.reduce((sum, s) => sum + s.score!, 0) / scored.length) : null;

  /* ── chart ── */
  const chartPoints = useMemo(() => {
    const cutoff = range === 'all' ? 0 : Date.now() - (range === '3m' ? 91 : 182) * 86400000;
    return scored.filter(s => new Date(s.date).getTime() >= cutoff).slice().reverse();
  }, [scored, range]);

  /* ── table ── */
  const rows = scans.filter(s => filter === 'all' || (filter === 'dropped' ? (s.change ?? 0) < 0 : s.scan_type === filter));
  const counts = {
    all: scans.length,
    fast: scans.filter(s => s.scan_type === 'fast').length,
    deep: scans.filter(s => s.scan_type === 'deep').length,
    dropped: scans.filter(s => (s.change ?? 0) < 0).length,
  };

  const toggleSelect = (id: string) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : prev.length >= 2 ? [prev[1], id] : [...prev, id]));
  const startCompare = () => {
    if (selected.length !== 2) return;
    // older scan is the baseline
    const [a, b] = selected.map(id => scans.find(s => s.id === id)!).sort((x, y) => new Date(x.date).getTime() - new Date(y.date).getTime());
    setCompareIds([a.id, b.id]);
  };

  const exportCsv = () => {
    const header = ['Scan ID', 'Date', 'Time', 'Type', 'Started by', 'Score', 'Risk level', 'Change', 'Critical', 'High', 'Medium', 'Low', 'Checks run'];
    const lines = scans.map(s => [s.ref || s.id, fmtDate(s.date), fmtTime(s.date), s.scan_type, triggerText(s.trigger) || '', s.score ?? '', s.risk_level ?? '',
      s.change ?? '', s.findings.critical, s.findings.high, s.findings.medium, s.findings.low, s.indicator_count]);
    const blob = new Blob([[header, ...lines].map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(vendorName || 'vendor').replace(/[^\w-]+/g, '_')}_scan_history.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (historyQuery.isPending) {
    return <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: c.muted }}>Loading scan history…</div>;
  }
  if (historyQuery.isError) {
    return (
      <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: c.red }}>
        {apiError(historyQuery.error, 'Could not load the scan history.')} <button style={linkBtn(isDark)} onClick={() => historyQuery.refetch()}>Retry</button>
      </div>
    );
  }

  const changeText = (v: number | null) => (v == null ? <span style={{ color: c.faint }}>—</span>
    : v === 0 ? <span style={{ color: c.muted }}>0</span>
    : <span style={{ color: v > 0 ? c.green : c.red, fontWeight: 700 }}>{v > 0 ? '+' : '−'}{Math.abs(v)}</span>);

  return (
    <div style={{ ...card(isDark), padding: 20, fontFamily: sans }}>
      <style>{`
        .vh-row:hover { background: ${c.soft}; }
        @keyframes vhFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      `}</style>

      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>Scan history</div>
          <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
            {scans.length ? `${scans.length} scan${scans.length === 1 ? '' : 's'} since ${fmtDate(scans[scans.length - 1].date)}` : 'No scans yet'}
          </div>
        </div>
        {scans.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              style={{ ...btn(selected.length === 2), opacity: scans.length < 2 ? 0.5 : 1 }}
              disabled={scans.length < 2}
              title={selected.length === 2 ? 'Compare the two selected scans' : 'Tick two scans in the table to compare them'}
              onClick={startCompare}
            ><Icon d={I.diff} size={13} />{selected.length === 2 ? 'Compare selected' : `Compare 2 scans${selected.length ? ` (${selected.length}/2)` : ''}`}</button>
            <button style={btn()} onClick={exportCsv}><Icon d={I.download} size={13} />Export</button>
          </div>
        )}
      </div>

      {scans.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0 24px' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', margin: '0 auto 10px', background: c.soft, color: c.faint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={I.scan} size={20} /></div>
          <div style={{ fontSize: 14, fontWeight: 700, color: c.ink }}>No scans yet</div>
          <div style={{ fontSize: 12.5, color: c.muted, marginTop: 4 }}>Run a scan from the top of the page, or set a schedule, and its results will build up here.</div>
        </div>
      ) : (
        <>
          {/* stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
            {[
              { label: 'Current score', value: latest?.score ?? '—', color: scoreColor(latest?.score ?? null),
                sub: latest?.change != null ? <span style={{ color: latest.change > 0 ? c.green : latest.change < 0 ? c.red : c.muted }}>{latest.change > 0 ? '+' : latest.change < 0 ? '−' : '±'}{Math.abs(latest.change)} vs previous</span> : 'First scan' },
              { label: 'Best / worst', value: best && worst ? `${best.score} / ${worst.score}` : '—', color: c.ink,
                sub: best && worst ? `${fmtDate(best.date).replace(/, \d{4}$/, '')} / ${fmtDate(worst.date).replace(/, \d{4}$/, '')}` : '' },
              { label: 'Average', value: average ?? '—', color: c.ink, sub: `across ${scored.length} scan${scored.length === 1 ? '' : 's'}` },
              { label: 'Last scan', value: latest ? relative(latest.date) : '—', color: c.ink,
                sub: latest ? [triggerText(latest.trigger), latest.scan_type].filter(Boolean).join(', ') : '' },
            ].map(tile => (
              <div key={tile.label} style={{ background: c.soft, border: `1px solid ${c.line}`, borderRadius: 9, padding: '12px 14px' }}>
                <div style={{ fontSize: 11.5, color: c.muted }}>{tile.label}</div>
                <div style={{ fontSize: 22, fontWeight: 750 as any, color: tile.color, marginTop: 3 }}>{tile.value}</div>
                <div style={{ fontSize: 11.5, color: c.faint, marginTop: 1 }}>{tile.sub}</div>
              </div>
            ))}
          </div>

          {/* trend chart */}
          <div style={{ border: `1px solid ${c.line}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: c.muted }}>Score over time</span>
              <span style={{ display: 'flex', gap: 2 }}>
                {(['3m', '6m', 'all'] as Range[]).map(r => (
                  <button key={r} onClick={() => setRange(r)} style={{
                    border: 'none', background: range === r ? c.soft : 'transparent', color: range === r ? c.accent : c.faint, fontWeight: 600, fontSize: 11.5,
                    padding: '3px 8px', borderRadius: 5, cursor: 'pointer', fontFamily: sans,
                  }}>{r === 'all' ? 'All' : r.toUpperCase()}</button>
                ))}
              </span>
            </div>
            <TrendChart points={chartPoints} c={c} onOpen={id => navigate(`/scans/${id}/results`)} />
          </div>

          {/* comparison panel */}
          {compareIds && (
            <ComparePanel
              query={compareQuery}
              c={c}
              isDark={isDark}
              onClose={() => { setCompareIds(null); setSelected([]); }}
            />
          )}

          {/* filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {([['all', 'All'], ['fast', 'Fast'], ['deep', 'Deep'], ['dropped', 'Score dropped']] as [Filter, string][]).map(([key, text]) => (
              <button key={key} onClick={() => { setFilter(key); setShown(15); }} style={btn(filter === key)}>
                {text}<span style={{ fontSize: 11, opacity: 0.7 }}>{counts[key]}</span>
              </button>
            ))}
            {selected.length > 0 && (
              <button style={{ ...linkBtn(isDark), marginLeft: 'auto', fontSize: 12 }} onClick={() => setSelected([])}>Clear selection</button>
            )}
          </div>

          {/* table */}
          <div style={{ border: `1px solid ${c.line}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead>
                  <tr style={{ background: c.soft }}>
                    {['', 'Date', 'Type', 'Score', 'Change', 'Findings', ''].map((h, i) => (
                      <th key={i} style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 600, color: c.faint, borderBottom: `1px solid ${c.line}`, width: i === 0 ? 36 : i === 6 ? 70 : undefined }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: '28px 0', textAlign: 'center', fontSize: 12.5, color: c.muted }}>No scans match this filter.</td></tr>
                  ) : rows.slice(0, shown).map(s => {
                    const isSel = selected.includes(s.id);
                    const trig = triggerText(s.trigger);
                    return (
                      <tr key={s.id} className="vh-row" style={{ borderBottom: `1px solid ${c.line}`, background: isSel ? (isDark ? 'rgba(59,130,246,.10)' : T.blueBg) : undefined }}>
                        <td style={{ padding: '10px 12px' }}>
                          <input type="checkbox" aria-label={`Select scan from ${fmtDate(s.date)}`} checked={isSel} onChange={() => toggleSelect(s.id)} style={{ cursor: 'pointer' }} />
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 13 }}>
                          <div style={{ fontWeight: 600, color: c.ink }}>{fmtDate(s.date)}</div>
                          <div style={{ fontSize: 11.5, color: c.faint, marginTop: 1 }}>
                            {s.ref && <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: c.muted }}>{s.ref} · </span>}
                            {fmtTime(s.date)}{trig ? ` · ${trig}` : ''}
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 12.5, color: c.ink }}>
                          <span style={pill(s.scan_type === 'deep' ? c.accent : c.muted, s.scan_type === 'deep' ? (isDark ? 'rgba(59,130,246,.14)' : T.blueBg) : c.soft)}>
                            {s.scan_type === 'deep' ? 'Deep' : 'Fast'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ fontSize: 15, fontWeight: 750 as any, color: scoreColor(s.score) }}>{s.score ?? '—'}</span>
                          {s.risk_level && <span style={{ fontSize: 11, color: c.faint, marginLeft: 5 }}>{s.risk_level}</span>}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 13 }}>{changeText(s.change)}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {s.findings.critical > 0 && <span style={redPill}>{s.findings.critical} critical</span>}
                            {s.findings.high > 0 && <span style={amberPill}>{s.findings.high} high</span>}
                            <span style={{ fontSize: 11.5, color: c.faint }}>{s.findings.medium} med · {s.findings.low} low</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <button style={{ ...linkBtn(isDark), fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 3 }} onClick={() => navigate(`/scans/${s.id}/results`)}>
                            View<Icon d={I.arrow} size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {rows.length > shown && (
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <button style={btn()} onClick={() => setShown(n => n + 15)}>Show {Math.min(15, rows.length - shown)} more</button>
            </div>
          )}
          {(historyQuery.data?.failed?.length ?? 0) > 0 && (
            <div style={{ marginTop: 14, border: `1px solid ${isDark ? '#3a1515' : '#FECACA'}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '9px 12px', fontSize: 12.5, fontWeight: 700, color: isDark ? '#F87171' : '#B91C1C', background: isDark ? '#1a0a0a' : '#FEF2F2' }}>
                Scans that didn't finish ({historyQuery.data!.failed!.length})
              </div>
              {historyQuery.data!.failed!.map(f => (
                <div key={f.id} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '9px 12px', borderTop: `1px solid ${c.line}`, fontSize: 12.5, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: c.muted, minWidth: 96 }}>{f.ref || f.id.slice(0, 8)}</span>
                  <span style={{ color: c.ink, minWidth: 150 }}>{fmtDate(f.date)} · {fmtTime(f.date)}{triggerText(f.trigger) ? ` · ${triggerText(f.trigger)}` : ''}</span>
                  <span style={{ color: c.muted, flex: 1, minWidth: 200 }}>{f.error || 'The scan stopped before finishing.'}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, color: c.faint, marginTop: 10 }}>
            Findings count checks rated low or above in that scan. How a scan was started is recorded for scans run since this page was added, and for earlier manual scans.
          </div>
        </>
      )}
    </div>
  );
}

/* ── Trend chart ───────────────────────────────────────────────────── */
function TrendChart({ points, c, onOpen }: { points: HistoryScan[]; c: any; onOpen: (id: string) => void }) {
  const [hover, setHover] = useState<number | null>(null);
  if (points.length === 0) return <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: c.faint }}>No scans in this period</div>;
  const W = 600, H = 130, PAD_L = 28, PAD_R = 10, PAD_T = 10, PAD_B = 22;
  const scores = points.map(p => p.score!);
  const lo = Math.max(0, Math.floor((Math.min(...scores) - 5) / 10) * 10);
  const hi = Math.min(100, Math.ceil((Math.max(...scores) + 5) / 10) * 10);
  const x = (i: number) => (points.length === 1 ? (PAD_L + W - PAD_R) / 2 : PAD_L + (i * (W - PAD_L - PAD_R)) / (points.length - 1));
  const y = (s: number) => PAD_T + ((hi - s) * (H - PAD_T - PAD_B)) / Math.max(hi - lo, 1);
  const grid = [hi, Math.round((hi + lo) / 2), lo];
  const h = hover != null ? points[hover] : null;
  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }} onMouseLeave={() => setHover(null)}>
        {grid.map(g => (
          <g key={g}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(g)} y2={y(g)} stroke={c.line} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
            <text x={PAD_L - 6} y={y(g) + 3} fontSize="10" textAnchor="end" fill={c.faint}>{g}</text>
          </g>
        ))}
        {points.length > 1 && <polyline fill="none" stroke={c.accent} strokeWidth="2" vectorEffect="non-scaling-stroke" points={points.map((p, i) => `${x(i)},${y(p.score!)}`).join(' ')} />}
        {points.map((p, i) => (
          <g key={p.id} style={{ cursor: 'pointer' }} onMouseEnter={() => setHover(i)} onClick={() => onOpen(p.id)}>
            <rect x={x(i) - 10} y={0} width={20} height={H} fill="transparent" />
            <circle cx={x(i)} cy={y(p.score!)} r={hover === i ? 5 : (p.change ?? 0) < 0 ? 4 : 3} fill={(p.change ?? 0) < 0 ? c.red : c.accent} stroke={c.surface} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          </g>
        ))}
        <text x={PAD_L} y={H - 4} fontSize="10" fill={c.faint}>{fmtDate(points[0].date)}</text>
        {points.length > 1 && <text x={W - PAD_R} y={H - 4} fontSize="10" textAnchor="end" fill={c.faint}>{fmtDate(points[points.length - 1].date)}</text>}
      </svg>
      {h && (
        <div style={{
          position: 'absolute', top: 0, left: `${(x(hover!) / W) * 100}%`, transform: `translateX(${x(hover!) > W * 0.7 ? '-105%' : '8px'})`,
          background: c.surface, border: `1px solid ${c.line}`, borderRadius: 7, padding: '6px 9px', fontSize: 11.5, color: c.ink, pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          <div style={{ fontWeight: 700 }}>{h.score} <span style={{ fontWeight: 400, color: c.faint }}>{h.change != null ? `(${h.change > 0 ? '+' : ''}${h.change})` : ''}</span></div>
          <div style={{ color: c.muted }}>{fmtDate(h.date)} · {h.scan_type}</div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: c.faint, marginTop: 4 }}>
        <span><span style={{ color: c.accent }}>●</span> Score</span><span><span style={{ color: c.red }}>●</span> Score dropped</span><span>Click a point to open that scan</span>
      </div>
    </div>
  );
}

/* ── Compare panel ─────────────────────────────────────────────────── */
function ComparePanel({ query, c, isDark, onClose }: { query: any; c: any; isDark: boolean; onClose: () => void }) {
  const data: Comparison | undefined = query.data;
  const list = (title: string, items: { indicator_name: string; severity: string }[] | undefined, color: string, sign: string) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 6 }}>{title} <span style={{ fontWeight: 600, color: c.faint }}>{items?.length || 0}</span></div>
      {items && items.length ? (
        <div style={{ display: 'grid', gap: 4 }}>
          {items.slice(0, 8).map(it => (
            <div key={it.indicator_name} style={{ fontSize: 12, color: c.ink, display: 'flex', gap: 6 }}>
              <span style={{ color, fontWeight: 700 }}>{sign}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.indicator_name}>{it.indicator_name}</span>
              <span style={{ color: c.faint, flexShrink: 0 }}>{it.severity}</span>
            </div>
          ))}
          {items.length > 8 && <div style={{ fontSize: 11.5, color: c.faint }}>and {items.length - 8} more</div>}
        </div>
      ) : <div style={{ fontSize: 12, color: c.faint }}>None</div>}
    </div>
  );
  return (
    <div style={{ border: `1px solid ${isDark ? '#1e3a8a' : T.blueBdr}`, background: isDark ? 'rgba(59,130,246,.05)' : '#F8FBFF', borderRadius: 10, padding: '14px 16px', marginBottom: 14, animation: 'vhFade .15s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>Comparing two scans</div>
          {data?.base_scan && data?.head_scan && (
            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
              {fmtDate(data.base_scan.date)} ({data.base_scan.score}) → {fmtDate(data.head_scan.date)} ({data.head_scan.score})
            </div>
          )}
        </div>
        <button onClick={onClose} aria-label="Close comparison" style={{ border: 'none', background: 'none', color: c.muted, cursor: 'pointer', padding: 2 }}><Icon d={I.close} size={15} /></button>
      </div>
      {query.isPending ? (
        <div style={{ fontSize: 12.5, color: c.muted }}>Comparing…</div>
      ) : query.isError ? (
        <div style={{ fontSize: 12.5, color: c.red }}>{apiError(query.error, 'Could not compare these scans.')}</div>
      ) : !data?.has_comparison ? (
        <div style={{ fontSize: 12.5, color: c.muted }}>{data?.message || 'Nothing to compare.'}</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14, fontSize: 12.5, color: c.muted }}>
            <span>Score <strong style={{ color: (data.score_change || 0) > 0 ? c.green : (data.score_change || 0) < 0 ? c.red : c.ink }}>{(data.score_change || 0) > 0 ? '+' : ''}{data.score_change}</strong></span>
            {data.severity && (['critical', 'high', 'medium', 'low'] as const).map(level => {
              const s = data.severity![level];
              const diff = s.to - s.from;
              return (
                <span key={level}>
                  {level[0].toUpperCase() + level.slice(1)} <strong style={{ color: c.ink }}>{s.from} → {s.to}</strong>
                  {diff !== 0 && <span style={{ color: diff > 0 ? c.red : c.green, fontWeight: 600 }}> ({diff > 0 ? '+' : ''}{diff})</span>}
                </span>
              );
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16 }}>
            {list('New findings', data.new_findings, c.red, '+')}
            {list('Resolved', data.resolved, c.green, '✓')}
            {list('Got worse', data.declined, c.amber, '↓')}
            {list('Got better', data.improved, c.accent, '↑')}
          </div>
        </>
      )}
    </div>
  );
}
